import React, {lazy, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {fetchJson} from '../../helpers/fetch.js';
import ColumnResizer from '../common/column_resizer.jsx';
import styles from './OptionManagement.module.scss';
import OptionGrid from './OptionGrid.lazy.jsx';
import withSuspense from '../../helpers/withSuspense.jsx';
import ButtonSkeleton from '../../skeleton_loaders/common/ButtonSkeleton.jsx';
import queryClient from '../../../query_client.js';
import { FaIcon } from '../common/Icons.jsx';
const NewOptionSetButton = withSuspense(
  lazy(() => import('./NewOptionSetButton.jsx')),
  <ButtonSkeleton size='sm' style={{width: '100%'}} />
);
const EditOptionSetCog = withSuspense(lazy(() => import('./EditOptionSetCog.jsx')), <FaIcon type='cog' />);

import {
  option_set_list_json_path,
  option_set_delete_path
} from '~/routes';

function OptionSets({projectId, optionSets, selectedOptionSetId, setSelectedOptionSetId, permissions}) {
  useEffect(() => {
    if (!optionSets.length) {
      setSelectedOptionSetId(null);
      return;
    }

    if (!optionSets.some((optionSet) => optionSet.id === selectedOptionSetId)) {
      setSelectedOptionSetId(optionSets[0].id);
    }
  }, [optionSets, selectedOptionSetId, setSelectedOptionSetId]);

  const deleteOptionSet = async (e, optionSet) => {
    e.stopPropagation();
    if (!confirm(I18n.t('option_set_delete_confirm_message', {name: optionSet.name}))) {
      return;
    }
    await fetchJson(option_set_delete_path({option_set_id: optionSet.id}), {method: 'POST'});
    queryClient.invalidateQueries(['optionSets']);
  }

  return (
    <div className={styles['option-sets-container']}>
      <div className={styles['actions']}>
        {permissions.create_option_set && <NewOptionSetButton projectId={projectId} />}
      </div>
      {optionSets.length ? <hr style={{margin: 0, marginBottom: '1rem'}} /> : null}
      <ul className={styles['list-group']}>
        {optionSets.map((optionSet) => (
          <button
            key={optionSet.id}
            className={`${styles['list-group-item']} list-group-item-action text-start cus ${selectedOptionSetId === optionSet.id ? styles['active'] : ''}`}
            onClick={() => setSelectedOptionSetId(optionSet.id)}
          >
            <div className={styles['info']}>
              <div>{optionSet.name} ({optionSet.usage_count})</div>
              <small className={selectedOptionSetId === optionSet.id ? '' : 'text-muted'}>
                {optionSet.key}
              </small>
            </div>
            <div className={styles['option-set-actions']}>
              {permissions.update_option_set && <EditOptionSetCog projectId={projectId} optionSetId={optionSet.id} />}
              {permissions.delete_option_set && !optionSet.usage_count && <FaIcon type='trash' className='text-danger' onClick={(e) => deleteOptionSet(e, optionSet)} />}
            </div>
          </button>
        ))}
      </ul>
    </div>
  );
}

function OptionManagement({projectId, permissions = {}}) {
  const [selectedOptionSetId, setSelectedOptionSetId] = useState(null);

  const {data, isLoading} = useQuery({
    queryKey: ['optionSets', projectId],
    queryFn: () => fetchJson(option_set_list_json_path())
  });

  const optionSets = data?.option_sets || [];

  const optionSet = useMemo(() => {
    return optionSets.find((row) => row.id === selectedOptionSetId) || null;
  }, [optionSets, selectedOptionSetId]);
  const rightWidth = Math.max(window.innerWidth * 0.3, 300);
  const initialLeftWidth = window.innerWidth - rightWidth;

  return (
    <div className={styles['option-management-container']} style={{'--action-bar-height': '47.6px'}}>
      <ColumnResizer
        minLeftWidth={100}
        minRightWidth={100}
        initialLeftWidth={initialLeftWidth}
      >
        {(isLoading || !!optionSets.length) && <OptionGrid optionSet={optionSet} projectId={projectId} gridHeight='calc(100vh - 300px)' permissions={permissions} />}
        <OptionSets
          projectId={projectId}
          optionSets={optionSets}
          selectedOptionSetId={selectedOptionSetId}
          setSelectedOptionSetId={setSelectedOptionSetId}
          permissions={permissions}
        />

      </ColumnResizer>
    </div>
  );
}

export default OptionManagement;